import { useParallax, useIntersectionObserver } from '../hooks/useParallax';

const TechStack = () => {
    const parallaxOffset = useParallax(0.15);
    const [ref, isIntersecting] = useIntersectionObserver({ threshold: 0.3 });

    const technologies = [
        { name: 'HTML', icon: null, materialIcon: 'code' },
        { name: 'CSS', icon: null, materialIcon: 'palette' },
        { name: 'JavaScript', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
        { name: 'React', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
        { name: 'Node.js', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
        { name: 'MongoDB', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg' },
        { name: 'Express.js', icon: null, materialIcon: 'data_object' },
        { name: 'Firebase', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg' },
        { name: 'Tailwind CSS', icon: null, materialIcon: 'brush' },
        { name: 'GitHub', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg' },
        { name: 'Vercel', icon: null, materialIcon: 'rocket_launch' },
        { name: 'Netlify', icon: null, materialIcon: 'cloud_upload' }
    ];

    return (
        <section
            ref={ref}
            className="pb-20 pt-6 border-t border-border-light dark:border-border-dark/30 bg-white/50 dark:bg-black/20 relative overflow-visible z-10"
        >
            {/* Parallax Background */}
            <div
                className="absolute inset-0 opacity-20 -z-10"
                style={{ transform: `translateY(${parallaxOffset}px)` }}
            >
                <div className="absolute top-10 right-20 w-32 h-32 bg-primary/20 rounded-full blur-2xl"></div>
                <div className="absolute bottom-10 left-20 w-40 h-40 bg-purple-500/20 rounded-full blur-2xl"></div>
            </div>

            <div className="container mx-auto px-6 text-center relative z-20">
                <div
                    className={`animate-fade-in-up transition-all duration-1000 ${isIntersecting ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                    style={{ transform: `translateY(${parallaxOffset * 0.5}px)` }}
                >
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                        Technological Foundation
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 ">
                        The modern tools I use to bring products to life.
                    </p>
                </div>

                <div
                    className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 -mt-4 relative z-30"
                    style={{ transform: `translateY(${parallaxOffset * 0.3}px)` }}
                >
                    {technologies.map((tech, index) => (
                        <div
                            key={index}
                            className={`flex items-center gap-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-700 px-3 sm:px-4 py-2.5 rounded-lg cursor-pointer hover:scale-105 hover:shadow-lg hover:border-primary/50 hover:bg-white dark:hover:bg-gray-700 transition-all duration-300 ${isIntersecting ? 'animate-fade-in-stagger' : 'opacity-0'}`}
                            style={{
                                animationDelay: `${index * 0.1}s`,
                                transform: `translateY(${Math.sin((index + parallaxOffset) * 0.1) * 3}px)`
                            }}
                        >
                            {tech.icon ? (
                                <img
                                    alt={tech.name}
                                    className={`w-5 h-5 ${tech.name === 'GitHub' ? 'dark:invert' : ''} hover:rotate-12 transition-transform duration-300`}
                                    src={tech.icon}
                                />
                            ) : (
                                <span className="material-icons-round text-gray-500 dark:text-gray-400 text-lg hover:text-primary hover:scale-110 transition-all duration-300">
                                    {tech.materialIcon}
                                </span>
                            )}
                            <span className="text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap">{tech.name}</span>
                        </div>
                    ))}
                </div>

                {/* Skills Progress Section */}
                <div
                    className={`mt-24 transition-all duration-1000 ${isIntersecting ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                    style={{ transform: `translateY(${parallaxOffset * 0.2}px)` }}
                >
                    <h3 className="text-xl  md:text-2xl font-semibold text-gray-900 dark:text-white mb-8 text-center">
                        Skills Proficiency
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                        {[
                            { name: 'Frontend Development', percentage: 90 },
                            { name: 'React', percentage: 85 },
                            { name: 'Backend Development', percentage: 75 },
                            { name: 'Database Management', percentage: 75 }
                        ].map((skill, index) => (
                            <div
                                key={index}
                                className={`bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200 dark:border-gray-700 p-4 rounded-lg ${isIntersecting ? 'animate-fade-in-stagger' : 'opacity-0'}`}
                                style={{ animationDelay: `${index * 0.15}s` }}
                            >
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                        {skill.name}
                                    </span>
                                    <span className="text-sm font-semibold text-primary">
                                        {skill.percentage}%
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                    <div
                                        className="bg-gradient-to-r from-primary to-purple-500 h-2 rounded-full transition-all duration-1000 ease-out"
                                        style={{
                                            width: isIntersecting ? `${skill.percentage}%` : '0%',
                                            transitionDelay: `${index * 0.15}s`
                                        }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TechStack;